import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { CompanyPhoneNumber } from '@/api/company'
import { useUpdatePhoneNumberSettings } from '@/api/phoneNumbers'
import { cn } from '@/lib/utils'

/**
 * L'adresse 911 d'un numéro, à part des autres réglages : son propre bouton,
 * juste sous l'adresse, et l'état réel chez Twilio.
 *
 * Le pays suit l'indicatif du numéro (le serveur le donne dans `pays_urgence`) :
 * provinces et code postal pour un numéro canadien, États et code ZIP pour un
 * numéro américain, que Twilio refuse d'enregistrer à une adresse canadienne.
 */

const PROVINCES: { code: string; fr: string; en: string }[] = [
  { code: 'AB', fr: 'Alberta', en: 'Alberta' },
  { code: 'BC', fr: 'Colombie-Britannique', en: 'British Columbia' },
  { code: 'MB', fr: 'Manitoba', en: 'Manitoba' },
  { code: 'NB', fr: 'Nouveau-Brunswick', en: 'New Brunswick' },
  { code: 'NL', fr: 'Terre-Neuve-et-Labrador', en: 'Newfoundland and Labrador' },
  { code: 'NS', fr: 'Nouvelle-Écosse', en: 'Nova Scotia' },
  { code: 'NT', fr: 'Territoires du Nord-Ouest', en: 'Northwest Territories' },
  { code: 'NU', fr: 'Nunavut', en: 'Nunavut' },
  { code: 'ON', fr: 'Ontario', en: 'Ontario' },
  { code: 'PE', fr: 'Île-du-Prince-Édouard', en: 'Prince Edward Island' },
  { code: 'QC', fr: 'Québec', en: 'Quebec' },
  { code: 'SK', fr: 'Saskatchewan', en: 'Saskatchewan' },
  { code: 'YT', fr: 'Yukon', en: 'Yukon' },
]

const ETATS: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky',
  LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico',
}

/** « British Columbia », « Qc » ou « QC » → « BC » / « QC » ; '' si inconnu. */
function codeRegion(valeur: string | null | undefined, pays: 'CA' | 'US'): string {
  const v = (valeur ?? '').trim()
  if (!v) return ''
  const haut = v.toUpperCase()
  if (pays === 'US') {
    if (ETATS[haut]) return haut
    return Object.entries(ETATS).find(([, nom]) => nom.toLowerCase() === v.toLowerCase())?.[0] ?? ''
  }
  if (PROVINCES.some((p) => p.code === haut)) return haut
  const sansAccent = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  return PROVINCES.find((p) => sansAccent(p.fr) === sansAccent(v) || sansAccent(p.en) === sansAccent(v))?.code ?? ''
}

type Statut = '' | 'en_cours' | 'declaree' | 'a_corriger' | 'sans_objet'

interface Props {
  number: CompanyPhoneNumber
  companyId: number
}

export function AdresseUrgence({ number, companyId }: Props) {
  const { t, i18n } = useTranslation()
  const k = (cle: string) => t(`manager.numbers.detail.${cle}`)
  const pays = number.pays_urgence ?? 'CA'
  const update = useUpdatePhoneNumberSettings(companyId, number.id)
  const queryClient = useQueryClient()

  const initial = () => ({
    address1: number.address1 ?? '',
    address2: number.address2 ?? '',
    city: number.city ?? '',
    province: codeRegion(number.province, pays),
    postal: number.postal ?? '',
  })
  const [edition, setEdition] = useState(false)
  const [champs, setChamps] = useState(initial)
  const [erreur, setErreur] = useState<'' | 'region' | 'postal' | 'incomplete'>('')
  useEffect(() => { setChamps(initial()); setEdition(false); setErreur('') }, [number.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // L'état réel chez Twilio : lu à l'ouverture, puis toutes les 15 s tant que c'est « en cours ».
  const etat = useQuery({
    queryKey: ['e911', companyId, number.id],
    queryFn: async () => (await api.get<{ statut: Statut; erreur: string }>(
      `/api/companies/${companyId}/phone-numbers/${number.id}/e911/`)).data,
    refetchInterval: (q) => (q.state.data?.statut === 'en_cours' ? 15000 : false),
    staleTime: 10000,
  })
  const statut: Statut = etat.data?.statut ?? number.e911_statut ?? ''

  const adresseLigne = [number.address1, number.address2, number.city, number.province, number.postal].filter(Boolean).join(', ')
  const badge = {
    declaree: { texte: k('e911StatutDeclaree'), ton: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' },
    en_cours: { texte: k('e911StatutEnCours'), ton: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300' },
    a_corriger: { texte: k('e911StatutACorriger'), ton: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300' },
    '': { texte: k('e911StatutAucune'), ton: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
    sans_objet: null,
  }[statut]

  const changer = (cle: keyof typeof champs, valeur: string) => {
    setChamps((c) => ({ ...c, [cle]: valeur }))
    setErreur('')
  }

  const declarer = async () => {
    if (!champs.address1.trim() || !champs.city.trim() || !champs.province || !champs.postal.trim()) {
      setErreur('incomplete')
      return
    }
    try {
      await update.mutateAsync({
        address1: champs.address1.trim(),
        address2: champs.address2.trim() || null,
        city: champs.city.trim(),
        province: champs.province,
        postal: champs.postal.trim(),
      })
      toast.success(k('e911Declaree'))
      setEdition(false)
      queryClient.invalidateQueries({ queryKey: ['e911', companyId, number.id] })
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; champ?: 'region' | 'postal' } } })?.response?.data
      if (data?.error === 'e911_address_invalid' && data.champ) setErreur(data.champ)
      else if (data?.error === 'e911_address_invalid') toast.error(k('e911AddressInvalid'))
      else toast.error(k('e911SyncFailed'))
    }
  }

  const libelleRegion = pays === 'US' ? k('e911Etat') : k('e911Province')
  const messageErreur =
    erreur === 'incomplete' ? k('e911Incomplete')
      : erreur === 'region' ? (pays === 'US' ? k('e911EtatInvalide') : k('e911RegionInvalide'))
        : erreur === 'postal' ? (pays === 'US' ? k('e911ZipInvalide') : k('e911PostalInvalide'))
          : ''

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white flex flex-wrap items-center gap-2">
            {k('e911Title')}
            {badge && <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', badge.ton)}>{badge.texte}</span>}
          </p>
          <p className={cn('text-xs', adresseLigne ? 'text-slate-500 dark:text-slate-400' : 'text-amber-700 dark:text-amber-400')}>
            {adresseLigne || k('e911Missing')}
          </p>
          {statut === 'en_cours' && <p className="text-xs text-slate-500 dark:text-slate-400">{k('e911EnCoursAide')}</p>}
          {statut === 'a_corriger' && <p className="text-xs text-red-600 dark:text-red-400">{k('e911RefusAide')}</p>}
        </div>
        {!edition && (
          <Button type="button" size="sm" variant="outline" className="flex-shrink-0" onClick={() => setEdition(true)}>
            {k('e911Edit')}
          </Button>
        )}
      </div>

      {edition && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
          <p className="text-xs text-slate-500 dark:text-slate-400">{k('e911Explication')}</p>
          {pays === 'US' && <p className="text-xs font-medium text-amber-700 dark:text-amber-400">{k('e911NumeroAmericain')}</p>}

          <Champ id="e911-rue" libelle={k('e911Address1')}>
            <Input id="e911-rue" className="h-9" value={champs.address1} onChange={(e) => changer('address1', e.target.value)} autoComplete="street-address" />
          </Champ>
          <Champ id="e911-complement" libelle={k('e911Address2')}>
            <Input id="e911-complement" className="h-9" value={champs.address2} onChange={(e) => changer('address2', e.target.value)} />
          </Champ>
          <Champ id="e911-ville" libelle={k('e911City')}>
            <Input id="e911-ville" className="h-9" value={champs.city} onChange={(e) => changer('city', e.target.value)} />
          </Champ>
          <Champ id="e911-region" libelle={libelleRegion} fautif={erreur === 'region'}>
            <Select value={champs.province} onValueChange={(v) => changer('province', v)}>
              <SelectTrigger id="e911-region" className={cn('h-9 w-full', erreur === 'region' && 'border-red-500')}>
                <SelectValue placeholder={pays === 'US' ? k('e911EtatPlaceholder') : k('e911ProvincePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {pays === 'US'
                  ? Object.entries(ETATS).map(([code, nom]) => <SelectItem key={code} value={code}>{nom} ({code})</SelectItem>)
                  : PROVINCES.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{i18n.language.startsWith('en') ? p.en : p.fr} ({p.code})</SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </Champ>
          <Champ id="e911-postal" libelle={pays === 'US' ? k('e911Zip') : k('e911Postal')} fautif={erreur === 'postal'}>
            <Input id="e911-postal" className={cn('h-9', erreur === 'postal' && 'border-red-500')} value={champs.postal}
              onChange={(e) => changer('postal', e.target.value)}
              placeholder={pays === 'US' ? k('e911ZipExemple') : k('e911PostalExemple')} autoComplete="postal-code" />
          </Champ>

          {messageErreur && <p className="text-xs text-red-600 dark:text-red-400">{messageErreur}</p>}

          <div className="flex items-center gap-2 pt-1">
            <Button type="button" size="sm" onClick={declarer} disabled={update.isPending}
              className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90">
              {update.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {k('e911Declarer')}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={update.isPending}
              onClick={() => { setChamps(initial()); setErreur(''); setEdition(false) }}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function Champ({ id, libelle, fautif, children }: { id: string; libelle: string; fautif?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className={cn('text-xs', fautif && 'text-red-600 dark:text-red-400')}>{libelle}</Label>
      {children}
    </div>
  )
}
